/* eslint-disable react/prop-types */
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, InputAdornment, IconButton, Pagination, Checkbox, FormControlLabel } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getAllWorkItems } from '../../../services/workItemService'
import { WorkItem } from '../../../types/Work/WorkItem'
import { StyledTableCell } from '../../core/StyledTableCell'
import { WorkItemRow } from './WorkItemRow'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useDebounced } from '../../hooks/useDebounced'
import { buildDescendantMap, getWorkItemPathById } from '../../../utils/WorkItemsTreeUtils'

interface WorkItemListProps {
	selectedWorkItems: { id: number, path: string }[]
	onSelectionChange: (workItems: { id: number, path: string }[]) => void
}

const ITEMS_PER_PAGE = 9

function toTree(wi: WorkItem, loaded: Map<number, WorkItem[]>): WorkItem {
	return {
		...wi,
		childrenWorkItems: (loaded.get(wi.id) ?? wi.childrenWorkItems ?? []).map((c) => toTree(c, loaded)),
	}
}

export default function WorkItemList({ selectedWorkItems, onSelectionChange }: WorkItemListProps) {
	const [workItems, setWorkItems] = useState<WorkItem[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [searchText, setSearchText] = useState<string>('')
	const [page, setPage] = useState<number>(1)
	const [allWorkItemsCount, setAllWorkItemsCount] = useState<number>(0)
	const [loadedChildren, setLoadedChildren] = useState<Map<number, WorkItem[]>>(new Map())

	const onChildrenLoaded = useCallback((parentId: number, children: WorkItem[] | undefined) => {
		setLoadedChildren((prev) => {
			const next = new Map(prev)
			if (children != null) next.set(parentId, children)
			else next.delete(parentId)
			return next
		})
	}, [])

	const fullTree = useMemo(
		() => workItems.map((w) => toTree(w, loadedChildren)),
		[workItems, loadedChildren],
	)

	const descendantMap = useMemo(() => buildDescendantMap(fullTree), [fullTree])

	const adjacentSelectedIds = useMemo(
		() => selectedWorkItems.flatMap((workItem) => descendantMap.get(workItem.id) ?? []),
		[selectedWorkItems, descendantMap],
	)

	const addSelectedWorkItem = useCallback(
		(workItemId: number) => {
			const descendants = descendantMap.get(workItemId) ?? []
			const alreadySelectedDescendants = descendants.filter((id) => selectedWorkItems.some((w) => w.id === id))
			onSelectionChange([
				...selectedWorkItems.filter((w) => !alreadySelectedDescendants.includes(w.id)),
				{ id: workItemId, path: getWorkItemPathById(fullTree, workItemId) ?? '' },
			])
		},
		[descendantMap, selectedWorkItems, fullTree, onSelectionChange],
	)

	const removeSelectedWorkItem = useCallback(
		(workItemId: number) => {
			onSelectionChange(selectedWorkItems.filter((w) => w.id !== workItemId))
		},
		[selectedWorkItems, onSelectionChange],
	)

	const getChildrenForParent = useCallback(
		(id: number) => loadedChildren.get(id),
		[loadedChildren],
	)

	// Fetch work items function
	const fetchWorkItems = useCallback(async (search: string, pageNumber: number) => {
		setLoading(true)
		try {
			const result = await getAllWorkItems(search, pageNumber, false)
			const items = result?.workItems || []
			setWorkItems(items)
			setAllWorkItemsCount(result?.allWorkItemsCount || 0)
		} catch (error) {
			console.error('Error fetching work items:', error)
			setWorkItems([])
			setAllWorkItemsCount(0)
		} finally {
			setLoading(false)
		}
	}, [])

	// Function to handle search with page reset
	const handleSearch = useCallback((search: string) => {
		setPage(1) // Reset to first page when search changes
		fetchWorkItems(search, 1)
	}, [fetchWorkItems])

	// Debounce the search function
	const debouncedHandleSearch = useDebounced(handleSearch, 500)

	// Handle search text changes
	useEffect(() => {
		debouncedHandleSearch(searchText)
	}, [searchText, debouncedHandleSearch])

	// Handle page changes
	useEffect(() => {
		fetchWorkItems(searchText, page)
	}, [page, fetchWorkItems, searchText])

	// Initial load
	useEffect(() => {
		fetchWorkItems('', 1)
	}, [fetchWorkItems])

	const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage)
	}



	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
			<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant='h6' sx={{ fontWeight: 600 }}>
					Select Work Item
				</Typography>
				<Pagination
					count={Math.ceil(allWorkItemsCount / ITEMS_PER_PAGE)}
					variant='outlined'
					shape='rounded'
					size='medium'
					onChange={handlePageChange}
					page={page}
				/>
				<TextField
					size='small'
					placeholder='Search work items...'
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					sx={{ width: '300px' }}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<SearchIcon />
							</InputAdornment>
						),
						endAdornment: searchText && (
							<InputAdornment position='end'>
								<IconButton
									size='small'
									onClick={() => setSearchText('')}
									aria-label='clear search'
								>
									<ClearIcon fontSize='small' />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Box>
			<TableContainer
				sx={{
					height: '84vh',
					width: '100%',
					overflowX: 'scroll',
					overflowY: 'scroll',
				}}
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<StyledTableCell align='center'>{/*<Box>
								 <FormControlLabel
									control={
										<Checkbox
											checked={selectedWorkItems.length === workItems?.length}
											onChange={() => {
												if (selectedWorkItems.length === workItems?.length) {
													onSelectionChange([])
												} else {
													onSelectionChange(workItems?.map((workItem) => ({ id: workItem.id, path: getWorkItemPathById(fullTree, workItem.id) ?? '' })) ?? [])
												}
											}}
											sx={{
												'& .MuiSvgIcon-root': {
													transition: 'transform 0.2s ease-in-out',
												},
												'&:hover .MuiSvgIcon-root': {
													transform: 'scale(1.1)',
												},
												p: 0,
											}}
										/>
									}
									label={
										<Typography variant='body2' color='text.primary' sx={{ ml: 1 }}>
											Select All
										</Typography>
									}
								/>
							</Box>*/}</StyledTableCell>
							<StyledTableCell align='center'>Type</StyledTableCell>
							<StyledTableCell align='center'>Craft</StyledTableCell>
							<StyledTableCell align='center'>ITP</StyledTableCell>
							<StyledTableCell>Name</StyledTableCell>
							<StyledTableCell align='center'>Attachments</StyledTableCell>
							<StyledTableCell align='center'>Release Date</StyledTableCell>
							<StyledTableCell align='center'>NOC Date</StyledTableCell>
							<StyledTableCell align='right'>Qty Budget</StyledTableCell>
							<StyledTableCell align='right'>Quantity Completed</StyledTableCell>
							<StyledTableCell align='right'>UOM</StyledTableCell>
							<StyledTableCell align='right'>Qty Earned</StyledTableCell>
							<StyledTableCell align='right'>Qty Remaining</StyledTableCell>
							<StyledTableCell align='right'>% Complete</StyledTableCell>
							<StyledTableCell align='right'>Qty Inspected</StyledTableCell>
							<StyledTableCell align='right'>Budgeted Hours</StyledTableCell>
							<StyledTableCell align='right'>Earned Hours</StyledTableCell>
							<StyledTableCell align='right'>Actual Hours</StyledTableCell>
							<StyledTableCell align='right'>PF</StyledTableCell>
							<StyledTableCell align='center'>Assigned To</StyledTableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell align='left' scope='row' colSpan={20}>
									<CircularProgress color='success' />
								</TableCell>
							</TableRow>
						) :
							(workItems ?? []).map((workItem: WorkItem) => (
								<WorkItemRow
									key={workItem.id}
									workItem={workItem}
									layer={1}
									selectedWorkItems={selectedWorkItems}
									adjacentSelectedIds={adjacentSelectedIds}
									addSelectedWorkItem={addSelectedWorkItem}
									removeSelectedWorkItem={removeSelectedWorkItem}
									onChildrenLoaded={onChildrenLoaded}
									getChildrenForParent={getChildrenForParent}
								/>
							))}
						{(workItems?.length ?? 0) === 0 && (
							<TableRow>
								<TableCell align='center' scope='row' colSpan={20}>
									<h4 style={{ fontStyle: 'italic' }}>No Work Items were found</h4>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	)
}
